// src/components/NotificationCenter.js
// The notification panel opened by the bell. Desktop = glass dropdown pinned
// top-right; mobile = glass sheet under the top bar. Loads the recipient's feed
// on open (paginated), drives the per-item "new" highlight (readAt), and navigates
// into the comment thread on tap.
//
// Read model (see adr-comment-reply-notifications.md):
//   • The bell resets the badge counter on open ("seen"). This component handles
//     the per-item readAt highlight ("read") — cleared on tap or "Mark all read".
//
// Deep-link target: /workouts?recording=<vodAssetId|recordingId>&comment=<replyId>.
// Before the Phase-5 receiver ships, that URL simply opens the recording; the
// &comment param is ignored. Graceful either way.

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { getNotifications, markRead, markManyRead } from '../services/commentNotifications';

// Compact relative-time (self-contained so we don't reach into ClassComments).
function timeAgo(iso) {
    if (!iso) return '';
    const then = new Date(iso).getTime();
    if (Number.isNaN(then)) return '';
    const secs = Math.floor((Date.now() - then) / 1000);
    if (secs < 60) return 'just now';
    const mins = Math.floor(secs / 60);
    if (mins < 60) return `${mins}m ago`;
    const hours = Math.floor(mins / 60);
    if (hours < 24) return `${hours}h ago`;
    const days = Math.floor(hours / 24);
    if (days < 7) return `${days}d ago`;
    return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

// 768px MUST match AppNav's desktop/mobile nav swap (index.css @media max-width:768px)
// so the panel layout never disagrees with which top bar / bell is actually visible.
const NAV_MOBILE_QUERY = '(max-width: 768px)';
const isMobileView = () => typeof window !== 'undefined'
    && window.matchMedia && window.matchMedia(NAV_MOBILE_QUERY).matches;

function NotificationCenter({ userId, open, onClose }) {
    const navigate = useNavigate();
    const [isMobile, setIsMobile] = useState(isMobileView());
    const [loading, setLoading] = useState(true);
    const [items, setItems] = useState([]);
    const [lastDoc, setLastDoc] = useState(null);
    const [hasMore, setHasMore] = useState(false);
    const [loadingMore, setLoadingMore] = useState(false);

    // Track viewport so the same component renders the right surface if the user
    // rotates / resizes while it's open.
    useEffect(() => {
        if (typeof window === 'undefined' || !window.matchMedia) return undefined;
        const mq = window.matchMedia(NAV_MOBILE_QUERY);
        const onChange = () => setIsMobile(mq.matches);
        // addEventListener is the modern API; addListener is the Safari<14 fallback.
        if (mq.addEventListener) mq.addEventListener('change', onChange);
        else if (mq.addListener) mq.addListener(onChange);
        return () => {
            if (mq.removeEventListener) mq.removeEventListener('change', onChange);
            else if (mq.removeListener) mq.removeListener(onChange);
        };
    }, []);

    // Load the feed each time the panel opens. Fresh load (not cached) so a user
    // who just received a reply sees it on open.
    useEffect(() => {
        if (!open || !userId) return;
        let cancelled = false;
        setLoading(true);
        getNotifications(userId, { pageSize: 20 }).then((res) => {
            if (cancelled) return;
            setItems(res.notifications);
            setLastDoc(res.lastDoc);
            setHasMore(res.hasMore);
            setLoading(false);
        });
        return () => { cancelled = true; };
    }, [open, userId]);

    // Close on Escape for keyboard/desktop parity.
    useEffect(() => {
        if (!open) return undefined;
        const onKey = (e) => { if (e.key === 'Escape') onClose(); };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [open, onClose]);

    const handleLoadMore = useCallback(async () => {
        if (loadingMore || !hasMore || !userId) return;
        setLoadingMore(true);
        const res = await getNotifications(userId, { pageSize: 20, lastDoc });
        setItems((prev) => {
            const seen = new Set(prev.map((p) => p.id));
            return [...prev, ...res.notifications.filter((n) => !seen.has(n.id))];
        });
        setLastDoc(res.lastDoc);
        setHasMore(res.hasMore);
        setLoadingMore(false);
    }, [loadingMore, hasMore, userId, lastDoc]);

    const handleOpenItem = useCallback((n) => {
        // Optimistic read highlight clear.
        if (!n.readAt) {
            setItems((prev) => prev.map((x) => x.id === n.id ? { ...x, readAt: new Date().toISOString() } : x));
            markRead(n.id);
        }
        const target = n.vodAssetId || n.recordingId || '';
        const commentAnchor = n.replyCommentId || n.commentId || '';
        // Destination resolution, in priority order:
        //   1. recording id — present when the comment/reply was posted in the On
        //      Demand modal. Direct, unchanged path.
        //   2. class id — comments posted on the POST-CLASS thread carry NO recording
        //      id: the thread unlocks at `ending`, but the VOD's muxAssetId only lands
        //      minutes later via the Mux webhook, so there is nothing to store at write
        //      time. Hand the class id to the Workouts receiver, which resolves it to
        //      the recording at CLICK time — by which point the VOD exists. This also
        //      retroactively repairs notifications already sitting in the feed (they
        //      all carry classId).
        //   3. neither — bare /workouts, as before.
        //   0. milestone_comment — a comment on the member's own milestone. Lives
        //      on the Wall, not in a class, so it routes to /milestones and never
        //      touches the class resolution below.
        let url = '/workouts';
        // replies_ready — the Reply Assistant nudge for the admin. Lives in Admin,
        // not in a class, so it routes straight to the Replies queue.
        if (n.type === 'replies_ready') {
            onClose();
            navigate('/admin?tab=replies');
            return;
        }
        // moderation_needed — member-replies moderation nudge for the admin.
        if (n.type === 'moderation_needed') {
            onClose();
            navigate('/admin?tab=moderation');
            return;
        }
        // purchase_attention — a program purchase didn't reach its member.
        if (n.type === 'purchase_attention') {
            onClose();
            navigate('/admin?tab=purchases');
            return;
        }
        if (n.type === 'milestone_comment') {
            onClose();
            navigate('/milestones');
            return;
        }
        if (target) {
            url = `/workouts?recording=${encodeURIComponent(target)}&comment=${encodeURIComponent(commentAnchor)}`;
        } else if (n.classId) {
            url = `/workouts?class=${encodeURIComponent(n.classId)}&comment=${encodeURIComponent(commentAnchor)}`;
        }
        onClose();
        navigate(url);
    }, [navigate, onClose]);

    const handleMarkAllRead = useCallback(() => {
        const unreadIds = items.filter((n) => !n.readAt).map((n) => n.id);
        if (unreadIds.length === 0) return;
        const stamp = new Date().toISOString();
        setItems((prev) => prev.map((n) => n.readAt ? n : { ...n, readAt: stamp }));
        markManyRead(unreadIds);
    }, [items]);

    if (!open) return null;

    const hasUnread = items.some((n) => !n.readAt);

    // ── Shared inner content (header + body) ──
    const content = (
        <>
            {/* Header */}
            <div style={{
                display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                padding: '16px 18px 12px', borderBottom: '1px solid var(--mom-cream-dark)',
            }}>
                <span style={{
                    fontFamily: 'var(--mom-font-serif)', fontSize: 18, fontStyle: 'italic',
                    fontWeight: 700, color: 'var(--mom-charcoal)',
                }}>Notifications</span>
                {hasUnread && (
                    <button
                        onClick={handleMarkAllRead}
                        style={{
                            background: 'none', border: 'none', cursor: 'pointer', padding: 0,
                            fontFamily: 'var(--mom-font-sans)', fontSize: 12, fontWeight: 600,
                            color: 'var(--mom-link)',
                        }}
                    >Mark all read</button>
                )}
            </div>

            {/* Body */}
            <div style={{ overflowY: 'auto', WebkitOverflowScrolling: 'touch', flex: 1 }}>
                {loading ? (
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '40px 0' }}>
                        <div style={{
                            width: 22, height: 22, borderRadius: '50%',
                            border: '2px solid var(--mom-cream-dark)', borderTopColor: 'var(--mom-primary)',
                            animation: 'spin 0.8s linear infinite',
                        }} />
                    </div>
                ) : items.length === 0 ? (
                    <div style={{
                        padding: '36px 24px', textAlign: 'center',
                        fontFamily: 'var(--mom-font-sans)', fontSize: 14,
                        color: 'var(--mom-warm-gray)', lineHeight: 1.6,
                    }}>
                        No new notifications right now.
                    </div>
                ) : (
                    <>
                        {items.map((n) => {
                            // Two notification kinds share this feed:
                            //   • coach_reply  → member sees "Danielle replied…" (avatar "D")
                            //   • new_comment  → admin sees "{member} commented" (member initial)
                            //   • milestone_comment → member sees "{member} cheered you on"
                            //   • replies_ready → admin sees "{count} replies ready for you" (avatar "D")
                            //   • member_reply → "{member} and N others replied to your comment" (combined, upserted)
                            //   • moderation_needed → admin sees "{count} comment(s) need your eye"
                            const isMilestone = n.type === 'milestone_comment';
                            const isRepliesReady = n.type === 'replies_ready' || n.type === 'moderation_needed' || n.type === 'purchase_attention';
                            const isMemberReply = n.type === 'member_reply';
                            const isNewComment = n.type === 'new_comment' || isMilestone;
                            const others = isMemberReply ? Math.max(0, (n.actorCount || 1) - 1) : 0;
                            const displayName = isRepliesReady
                                ? (n.title || 'Replies ready for you')
                                : isMemberReply
                                    ? (others > 0 ? `${n.fromName || 'A member'} and ${others} other${others === 1 ? '' : 's'}` : (n.fromName || 'A member'))
                                    : (isNewComment ? (n.fromName || 'A member') : (n.fromName || 'Danielle'));
                            const avatarInitial = (isNewComment || isMemberReply)
                                ? ((n.fromName || displayName).trim().charAt(0).toUpperCase() || '?')
                                : 'D';
                            const bodyExcerpt = isRepliesReady ? null : (isNewComment ? n.commentExcerpt : n.replyExcerpt);
                            const actionLabel = isRepliesReady
                                ? ''
                                : (isMilestone
                                    ? ` cheered on your ${n.badgeName || 'milestone'}`
                                    : (isNewComment ? ' commented' : ' replied to your comment'));
                            return (
                                <button
                                    key={n.id}
                                    onClick={() => handleOpenItem(n)}
                                    style={{
                                        display: 'flex', gap: 10, alignItems: 'flex-start', width: '100%',
                                        textAlign: 'left', border: 'none', cursor: 'pointer',
                                        padding: '12px 18px',
                                        borderBottom: '1px solid var(--mom-cream-dark)',
                                        background: n.readAt ? 'transparent' : 'color-mix(in srgb, var(--mom-primary) 7%, transparent)',
                                        transition: 'background 0.15s ease',
                                    }}
                                >
                                    {/* Avatar — coach gets the "D" gradient; member comments get the member's initial */}
                                    <div style={{
                                        width: 34, height: 34, borderRadius: '50%', flexShrink: 0,
                                        background: 'linear-gradient(135deg, var(--mom-primary), var(--mom-primary-dark))',
                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    }}>
                                        <span style={{
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, fontWeight: 700, color: '#fff',
                                        }}>{avatarInitial}</span>
                                    </div>
                                    {/* Text */}
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)',
                                            lineHeight: 1.45,
                                        }}>
                                            <strong style={{ fontWeight: 700 }}>{displayName}</strong>{actionLabel}
                                        </div>
                                        {bodyExcerpt && (
                                            <div style={{
                                                fontFamily: 'var(--mom-font-sans)', fontSize: 13, color: 'var(--mom-charcoal)',
                                                opacity: 0.85, lineHeight: 1.45, marginTop: 2,
                                                display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical',
                                                overflow: 'hidden', wordBreak: 'break-word',
                                            }}>“{bodyExcerpt}”</div>
                                        )}
                                        {!isNewComment && n.parentExcerpt && (
                                            <div style={{
                                                fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)',
                                                lineHeight: 1.4, marginTop: 3,
                                                whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
                                            }}>on: {n.parentExcerpt}</div>
                                        )}
                                        <div style={{
                                            fontFamily: 'var(--mom-font-sans)', fontSize: 11, color: 'var(--mom-warm-gray)',
                                            marginTop: 4,
                                        }}>{timeAgo(n.createdAt)}</div>
                                    </div>
                                    {/* Unread dot */}
                                    {!n.readAt && (
                                        <div style={{
                                            width: 8, height: 8, borderRadius: '50%', flexShrink: 0, marginTop: 6,
                                            background: 'var(--mom-primary)',
                                        }} />
                                    )}
                                </button>
                            );
                        })}
                        {hasMore && (
                            <button
                                onClick={handleLoadMore}
                                disabled={loadingMore}
                                style={{
                                    width: '100%', background: 'none', border: 'none', cursor: 'pointer',
                                    padding: '14px 0', fontFamily: 'var(--mom-font-sans)', fontSize: 12,
                                    fontWeight: 600, color: 'var(--mom-link)', opacity: loadingMore ? 0.5 : 1,
                                }}
                            >{loadingMore ? 'Loading…' : 'Load older'}</button>
                        )}
                    </>
                )}
            </div>
        </>
    );

    // ── Backdrop (click-out to close) + responsive surface ──
    // Portaled to <body> so the fixed positioning resolves against the VIEWPORT.
    // The bell lives inside the nav (which has backdrop-filter) and the mobile bell
    // wrapper has a transform — both create containing blocks that would otherwise
    // trap a position:fixed child and shrink it to a sliver.
    const overlay = (
        <div
            onClick={onClose}
            style={{
                position: 'fixed', inset: 0, zIndex: 9998,
                background: isMobile ? 'rgba(40,30,30,0.28)' : 'transparent',
                animation: 'fadeIn 0.15s ease-out',
            }}
        >
            <div
                onClick={(e) => e.stopPropagation()}
                style={isMobile ? {
                    // Mobile: glass sheet under the 52px top bar, leaves room above the bottom nav.
                    position: 'fixed', top: 52, left: 8, right: 8,
                    maxHeight: 'calc(100vh - 52px - 88px)',
                    display: 'flex', flexDirection: 'column',
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                    border: '1px solid rgba(255,255,255,0.6)', borderRadius: 18,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.18)', overflow: 'hidden',
                    animation: 'slideUp 0.22s ease-out',
                } : {
                    // Desktop: dropdown pinned just under the 70px top bar, top-right.
                    position: 'fixed', top: 74, right: 20, width: 380,
                    maxHeight: '70vh', display: 'flex', flexDirection: 'column',
                    background: 'rgba(255,255,255,0.92)',
                    backdropFilter: 'blur(24px) saturate(1.4)', WebkitBackdropFilter: 'blur(24px) saturate(1.4)',
                    border: '1px solid rgba(255,255,255,0.6)', borderRadius: 16,
                    boxShadow: '0 24px 60px rgba(0,0,0,0.16)', overflow: 'hidden',
                    animation: 'fadeIn 0.18s ease-out',
                }}
            >
                {content}
            </div>
        </div>
    );

    return (typeof document !== 'undefined' && document.body)
        ? createPortal(overlay, document.body)
        : overlay;
}

export default NotificationCenter;
